/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';
import { Button, Input, Rate, Typography, message } from 'antd';
import { LikeOutlined, DislikeOutlined } from '@ant-design/icons';
import TextArea from 'antd/lib/input/TextArea';
import { useIntl } from 'react-intl';
import { useMutation, queryCache } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import type {
  BinaryReview,
  StarReview,
  ReviewConfig,
  ReviewServiceFunctions,
} from './types';

type ReviewFormProps = {
  entityId: number;
  contextIds: Record<string, number | string>;
  config: ReviewConfig;
  upsertReview: ReviewServiceFunctions['upsertReview'];
  existingReview?: BinaryReview | StarReview | null;
  onSubmitSuccess: () => void;
  onCancel?: () => void;
};

export const ReviewForm: React.FC<ReviewFormProps> = ({
  entityId,
  contextIds,
  config,
  upsertReview,
  existingReview,
  onSubmitSuccess,
  onCancel,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const isBinary = config.ratingType === 'binary';

  const [binaryRating, setBinaryRating] = useState<boolean | null>(
    existingReview && 'binary_rating' in existingReview
      ? existingReview.binary_rating
      : null
  );

  const [starRating, setStarRating] = useState<number>(
    existingReview && 'star_rating' in existingReview
      ? existingReview.star_rating
      : 0
  );

  const [title, setTitle] = useState(existingReview?.title || '');
  const [content, setContent] = useState(existingReview?.content || '');
  const [summary, setSummary] = useState(existingReview?.summary || '');

  const [errors, setErrors] = useState<{
    rating?: string;
    title?: string;
    content?: string;
    summary?: string;
  }>({});

  useEffect(() => {
    if (existingReview) {
      if ('binary_rating' in existingReview) {
        setBinaryRating(existingReview.binary_rating);
      }
      if ('star_rating' in existingReview) {
        setStarRating(existingReview.star_rating);
      }
      setTitle(existingReview.title || '');
      setContent(existingReview.content || '');
      setSummary(existingReview.summary || '');
    }
  }, [existingReview]);

  const queryKey = [
    config.queryKeyPrefix,
    ...Object.values(contextIds),
    entityId,
  ];

  const [doUpsert, { isLoading: isSaving }] = useMutation(upsertReview, {
    onSuccess: () => {
      message.success(
        intl.formatMessage({
          id: existingReview
            ? 'review-updated-success'
            : 'review-created-success',
        })
      );
      queryCache.invalidateQueries(queryKey);
      onSubmitSuccess();
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'error' }));
    },
  });

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (isBinary && binaryRating === null) {
      newErrors.rating = intl.formatMessage({
        id: 'review-validation-rating-required',
      });
    }

    if (!isBinary && starRating === 0) {
      newErrors.rating = intl.formatMessage({
        id: 'review-validation-rating-required',
      });
    }

    if (!title.trim()) {
      newErrors.title = intl.formatMessage({
        id: 'review-validation-title-required',
      });
    } else if (title.length > 100) {
      newErrors.title = intl.formatMessage({
        id: 'review-validation-title-max',
      });
    }

    if (content && content.length > 500) {
      newErrors.content = intl.formatMessage({
        id: 'review-validation-content-max',
      });
    }

    if (summary && summary.length > 150) {
      newErrors.summary = intl.formatMessage({
        id: 'review-validation-summary-max',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const baseBody = {
      title: title.trim(),
      content: content.trim() || null,
      summary: summary.trim() || null,
    };

    if (isBinary && binaryRating !== null) {
      doUpsert({
        entityId,
        body: {
          ...baseBody,
          binary_rating: binaryRating,
        },
      });
    } else if (!isBinary && starRating > 0) {
      doUpsert({
        entityId,
        body: {
          ...baseBody,
          star_rating: starRating as 1 | 2 | 3 | 4 | 5,
        },
      });
    }
  };

  return (
    <div css={styles.form}>
      <Typography.Title level={5} css={styles.formTitle}>
        {intl.formatMessage({ id: 'review-your-review' })}
      </Typography.Title>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'review-rating-label' })} *
        </Typography.Text>

        {isBinary ? (
          <div css={styles.binaryButtons}>
            <Button
              size="large"
              type={binaryRating === true ? 'primary' : 'default'}
              icon={<LikeOutlined rev={''} />}
              onClick={() => setBinaryRating(true)}
              css={[
                styles.binaryButton,
                binaryRating === true && styles.binaryButtonActive,
              ]}>
              {intl.formatMessage({ id: 'review-thumbs-up' })}
            </Button>
            <Button
              size="large"
              type={binaryRating === false ? 'primary' : 'default'}
              danger={binaryRating === false}
              icon={<DislikeOutlined rev={''} />}
              onClick={() => setBinaryRating(false)}
              css={[
                styles.binaryButton,
                binaryRating === false && styles.binaryButtonActiveDanger,
              ]}>
              {intl.formatMessage({ id: 'review-thumbs-down' })}
            </Button>
          </div>
        ) : (
          <Rate
            value={starRating}
            onChange={setStarRating}
            css={styles.starRating}
          />
        )}

        {errors.rating && (
          <Typography.Text type="danger" css={styles.errorText}>
            {errors.rating}
          </Typography.Text>
        )}
      </div>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'review-title-label' })} *
        </Typography.Text>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={intl.formatMessage({ id: 'review-title-placeholder' })}
          maxLength={100}
          showCount
          status={errors.title ? 'error' : ''}
        />
        {errors.title && (
          <Typography.Text type="danger" css={styles.errorText}>
            {errors.title}
          </Typography.Text>
        )}
      </div>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'review-content-label' })}
        </Typography.Text>
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'review-content-placeholder',
          })}
          rows={4}
          maxLength={500}
          showCount
          status={errors.content ? 'error' : ''}
        />
        {errors.content && (
          <Typography.Text type="danger" css={styles.errorText}>
            {errors.content}
          </Typography.Text>
        )}
      </div>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'review-summary-label' })}
        </Typography.Text>
        <Input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'review-summary-placeholder',
          })}
          maxLength={150}
          status={errors.summary ? 'error' : ''}
        />
        {errors.summary && (
          <Typography.Text type="danger" css={styles.errorText}>
            {errors.summary}
          </Typography.Text>
        )}
      </div>

      <div css={styles.formActions}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isSaving}>
            {intl.formatMessage({ id: 'review-cancel-button' })}
          </Button>
        )}
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={isSaving}>
          {intl.formatMessage({ id: 'review-save-button' })}
        </Button>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  form: {
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
  },

  formTitle: {
    marginTop: 0,
    marginBottom: 16,
    fontSize: 16,
  },

  formField: {
    marginBottom: 16,

    '&:last-of-type': {
      marginBottom: 0,
    },
  },

  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: 8,
  },

  binaryButtons: {
    display: 'flex',
    gap: 12,
  },

  binaryButton: {
    flex: 1,
    height: 48,
  },

  binaryButtonActive: {
    background: '#52c41a',
    borderColor: '#52c41a',

    '&:hover': {
      background: '#73d13d',
      borderColor: '#73d13d',
    },
  },

  binaryButtonActiveDanger: {
    background: '#ff4d4f',
    borderColor: '#ff4d4f',

    '&:hover': {
      background: '#ff7875',
      borderColor: '#ff7875',
    },
  },

  starRating: {
    fontSize: 28,
  },

  errorText: {
    display: 'block',
    fontSize: 12,
    marginTop: 4,
  },

  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 20,
  },
});
